/**
 * PlanCard - Displays current subscription plan with usage information.
 */

import {
  Accordion,
  Avatar,
  Badge,
  Button,
  Flex,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  ActionIcon,
  ThemeIcon,
  Divider,
} from "@mantine/core";
import {
  IconFolderBolt,
  IconInfoCircle,
  IconPackage,
  IconUsers,
} from "@tabler/icons-react";
import type {
  CommercialAddOnBillingRow,
  CreditGrantBillingRow,
} from "@/utils/billing/types";
import type { CurrentPlan } from "../types";

interface PlanCardProps {
  currentPlan: CurrentPlan;
  onManageBilling: () => void;
  onPurchaseCredits?: () => void;
  /** Opens commercial add-on purchase (contract customers with catalog offerings). */
  onPurchaseCommercialAddOns?: () => void;
}

export const PlanCard = ({
  currentPlan,
  onManageBilling,
  onPurchaseCredits,
  onPurchaseCommercialAddOns,
}: PlanCardProps) => {
  const isContractCommitment =
    currentPlan?.billingModel === "contract_commitment";
  const contractDetails = isContractCommitment
    ? currentPlan?.billingDetails
    : null;
  const isFreeTrial = currentPlan?.name.toLowerCase() === "free trial";
  const hasMeteredPricing = !!currentPlan?.meteredAnnualPrice;

  // Usage data from the current billing period
  const usage = currentPlan?.usage;
  const prepaidUsage = usage?.prepaidUsage ?? 0;
  const payLaterUsage = usage?.payLaterUsage ?? 0;

  // Credit calculations
  const currentCredits = currentPlan?.currentCredits ?? 0;
  const businessPoolCredits =
    currentPlan?.businessPoolCredits ?? currentCredits;
  const teamMemberCredits = currentPlan?.teamMemberCredits ?? 0;

  // Check if we have team member credits to show
  const hasTeamMemberCredits = teamMemberCredits > 0;

  // Progress bar calculations - show currentCredits out of total (currentCredits + prepaidUsage)
  const totalCreditsForMeter = currentCredits + prepaidUsage;

  // Calculate percentages for each section of the progress bar
  // Order: business pool (blue) | team member (teal) | used (gray)
  const businessPoolPercentage =
    totalCreditsForMeter > 0
      ? (businessPoolCredits / totalCreditsForMeter) * 100
      : 0;
  const teamMemberPercentage =
    totalCreditsForMeter > 0
      ? (teamMemberCredits / totalCreditsForMeter) * 100
      : 0;
  const usagePercentage =
    totalCreditsForMeter > 0 ? (prepaidUsage / totalCreditsForMeter) * 100 : 0;

  // Should we show the progress bar? Only if there are credits or usage
  const showProgressBar = totalCreditsForMeter > 0;

  // Should we show pay later usage counter?
  const showPayLaterUsage = payLaterUsage > 0;

  const contractAvailableCredits =
    contractDetails?.availableCredits ?? currentCredits;
  const contractCommittedCredits = contractDetails?.committedCredits ?? 0;
  /** Cumulative grant burns this term (usage on the contract), not “spent vs available” balance. */
  const commitmentBurnCredits = contractDetails?.commitmentBurnCredits ?? 0;
  const contractDepositCredits = contractDetails?.depositCredits ?? 0;
  const contractRemainingToCommitment = Math.max(
    contractCommittedCredits - commitmentBurnCredits,
    0
  );
  const isPrepaidCreditsExhausted = contractAvailableCredits <= 0;
  const contractCommitmentRemainingPercentage =
    contractCommittedCredits > 0
      ? (contractRemainingToCommitment / contractCommittedCredits) * 100
      : 0;
  const contractCommitmentUsedPercentage =
    contractCommittedCredits > 0
      ? (commitmentBurnCredits / contractCommittedCredits) * 100
      : 0;

  /** Blended $/credit: backend field, or derived from totals when present. */
  const effectiveUnitPriceCents =
    contractDetails?.effectiveUnitPriceCents ??
    (contractDetails &&
    contractDetails.committedCredits > 0 &&
    contractDetails.totalCommittedValueCents != null
      ? Math.round(
          contractDetails.totalCommittedValueCents /
            contractDetails.committedCredits
        )
      : null);

  /** Paid commercial add-on lines (shown as “applied” badges on the Add-on Features card). */
  const appliedCommercialAddOns: CommercialAddOnBillingRow[] =
    contractDetails?.commercialAddOns?.filter((row) => row.status === "paid") ??
    [];

  const totalCommercialAddOnValueCents =
    contractDetails?.totalCommercialAddOnValueCents ?? 0;
  const totalContractValueCents =
    contractDetails?.totalContractValueCents ??
    (contractDetails?.totalCommittedValueCents ?? 0) +
      totalCommercialAddOnValueCents;

  const formatDate = (value?: string | null): string => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatUsdFromCents = (cents: number): string =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);

  /** Catalog image URL for commercial add-on badge avatars (camelCase or snake_case). */
  const commercialAddOnAvatarSrc = (
    row: CommercialAddOnBillingRow
  ): string | undefined => {
    const raw = row.displayImageUrl ?? row.display_image_url ?? "";
    const s = typeof raw === "string" ? raw.trim() : "";
    return s || undefined;
  };

  return (
    <Paper radius="md" p={0} maw={1000} mt="xl">
      <Stack gap="md">
        {/* Plan Header with Usage */}
        <Paper radius="md" p="lg" shadow="none" withBorder>
          <Flex gap="sm" align="flex-start" justify="space-between" wrap="wrap">
            <Group gap="xs" align="center" justify="space-between" w="100%">
              <Text fz={18} fw={700}>
                {currentPlan?.name || "No active plan"}
              </Text>
              {currentPlan?.nextInvoiceDate && !isFreeTrial ? (
                <Badge color="blue.8" variant="light" mt="2px">
                  <Text fw={500} fz={12} c="blue.8">
                    Next payment:{" "}
                    {new Date(
                      currentPlan.nextInvoiceDate * 1000
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </Text>
                </Badge>
              ) : isFreeTrial ? (
                <Badge color="blue.4" variant="light" size="md">
                  TRIAL
                </Badge>
              ) : null}
              {hasMeteredPricing && (
                <Tooltip
                  label={
                    <Text size="sm" c="white">
                      You will be charged per return. Payment will be deducted
                      from your bank product info if provided and available,
                      otherwise your card on file will be charged.
                    </Text>
                  }
                  multiline
                  w={300}
                  withArrow
                  position="right"
                >
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Flex>
        </Paper>
        <Paper p="lg" radius="md" shadow="none" bg="blue.0">
          {isContractCommitment ? (
            <Stack gap="sm" p="sm">
              <Text fz={20} fw={700} lh={1.1} c="dark.8">
                {isPrepaidCreditsExhausted
                  ? `${contractRemainingToCommitment.toLocaleString()} credits remaining in your planned usage`
                  : `${contractAvailableCredits.toLocaleString()} credits available now`}
              </Text>
              <Text size="md" c="blue.9" fw={500}>
                {isPrepaidCreditsExhausted
                  ? "Prepaid credits have been fully used"
                  : `From your ${contractDepositCredits.toLocaleString()}-credit deposit`}
              </Text>

              <Progress.Root radius="sm" size="md">
                <Progress.Section
                  value={contractCommitmentUsedPercentage}
                  color="blue.7"
                />
                <Progress.Section
                  value={contractCommitmentRemainingPercentage}
                  color="blue.2"
                />
              </Progress.Root>

              <Group gap="xs" align="flex-start" wrap="nowrap">
                <Text size="md" c="dark.7" style={{ flex: 1 }}>
                  {isPrepaidCreditsExhausted ? (
                    <>
                      <Text span fw={700} c="dark.9">
                        {commitmentBurnCredits.toLocaleString()} of{" "}
                        {contractCommittedCredits.toLocaleString()}
                      </Text>{" "}
                      credits applied to your plan this term
                    </>
                  ) : (
                    <>
                      Credits applied to your plan:{" "}
                      <Text span fw={700} c="dark.9">
                        {commitmentBurnCredits.toLocaleString()} /{" "}
                        {contractCommittedCredits.toLocaleString()}
                      </Text>{" "}
                      credits
                    </>
                  )}
                </Text>
                <Tooltip
                  label={
                    <Text size="sm" c="white">
                      "Credits applied to your plan” shows how many credits have
                      been applied to your plan during this term. “Credits
                      available now” shows your current credit balance.
                    </Text>
                  }
                  multiline
                  w={300}
                  withArrow
                >
                  <ActionIcon variant="subtle" color="gray" size="sm" mt={2}>
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Accordion
                variant="contained"
                radius="sm"
                styles={{
                  item: { background: "transparent", border: "none" },
                  control: { paddingLeft: 0, paddingRight: 0 },
                  label: {
                    color: "var(--mantine-color-blue-8)",
                    fontWeight: 600,
                    fontSize: "1.05rem",
                  },
                  content: { paddingLeft: 0, paddingRight: 0, paddingTop: 0 },
                  panel: { paddingTop: 4 },
                }}
              >
                <Accordion.Item value="contract-details" bg="blue.0">
                  <Accordion.Control px="md">Plan details</Accordion.Control>
                  <Accordion.Panel>
                    <Paper radius="sm" p="md" bg="blue.0">
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                        <Stack gap={8}>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="md" c="dark.6">
                              Initial deposit credits:
                            </Text>
                            <Text size="md" fw={600} c="dark.8">
                              {contractDepositCredits.toLocaleString()}
                            </Text>
                          </Group>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="md" c="dark.6">
                              Total planned usage:
                            </Text>
                            <Text size="md" fw={600} c="dark.8">
                              {contractCommittedCredits.toLocaleString()}
                            </Text>
                          </Group>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="md" c="dark.6">
                              Plan term end:
                            </Text>
                            <Text size="md" fw={600} c="dark.8">
                              {formatDate(contractDetails?.termEnd)}
                            </Text>
                          </Group>
                        </Stack>

                        <Stack gap={8}>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="md" c="dark.6">
                              Applied to planned usage:
                            </Text>
                            <Text size="md" fw={600} c="dark.8">
                              {commitmentBurnCredits.toLocaleString()}
                            </Text>
                          </Group>
                          <Group justify="space-between" wrap="nowrap">
                            <Text size="md" c="dark.6">
                              Remaining planned usage:
                            </Text>
                            <Text size="md" fw={600} c="dark.8">
                              {contractRemainingToCommitment.toLocaleString()}
                            </Text>
                          </Group>
                          <Stack gap={4}>
                            <Group justify="space-between" wrap="nowrap">
                              <Group gap={4} align="center" wrap="nowrap">
                                <Text size="md" c="dark.6">
                                  Effective rate (blended):
                                </Text>
                                <Tooltip
                                  label={
                                    <Text size="sm" c="white">
                                      Average price per credit across your
                                      planned usage. Individual blocks may use
                                      different contract rates.
                                    </Text>
                                  }
                                  multiline
                                  w={280}
                                  withArrow
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    aria-label="About effective rate"
                                  >
                                    <IconInfoCircle size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                              <Text size="md" fw={600} c="dark.8">
                                {effectiveUnitPriceCents != null
                                  ? `${formatUsdFromCents(effectiveUnitPriceCents)}/credit`
                                  : "N/A"}
                              </Text>
                            </Group>
                            <Text size="xs" c="dimmed">
                              Total committed value divided by total planned
                              credits.
                            </Text>
                          </Stack>
                          {contractDetails?.totalCommittedValueCents !=
                            null && (
                            <>
                              <Divider my="sm" />
                              <Group justify="space-between" wrap="nowrap">
                                <Text size="md" c="dark.6">
                                  Credit commitment value:
                                </Text>
                                <Text size="md" fw={600} c="dark.8">
                                  {formatUsdFromCents(
                                    contractDetails.totalCommittedValueCents
                                  )}
                                </Text>
                              </Group>
                              {totalCommercialAddOnValueCents > 0 && (
                                <Group justify="space-between" wrap="nowrap">
                                  <Text size="md" c="dark.6">
                                    Additional services:
                                  </Text>
                                  <Text size="md" fw={600} c="dark.8">
                                    {formatUsdFromCents(
                                      totalCommercialAddOnValueCents
                                    )}
                                  </Text>
                                </Group>
                              )}
                              <Group justify="space-between" wrap="nowrap">
                                <Text size="md" c="dark.6">
                                  Total contract value:
                                </Text>
                                <Text size="md" fw={600} c="dark.8">
                                  {formatUsdFromCents(totalContractValueCents)}
                                </Text>
                              </Group>
                            </>
                          )}
                        </Stack>
                      </SimpleGrid>
                      {contractDetails?.creditGrants &&
                        contractDetails.creditGrants.length > 0 && (
                          <>
                            <Divider my="md" />
                            <Stack gap="sm">
                              <Text size="sm" fw={600} c="dark.7">
                                Planned Usage Blocks
                              </Text>
                              {contractDetails.creditGrants.map(
                                (
                                  grant: CreditGrantBillingRow,
                                  index: number
                                ) => {
                                  const addOnOrdinal = contractDetails
                                    .creditGrants!.slice(0, index + 1)
                                    .filter(
                                      (row: CreditGrantBillingRow) =>
                                        row.grantRole === "add_on"
                                    ).length;
                                  const blockLabel =
                                    grant.grantRole === "base"
                                      ? "Original Plan"
                                      : `Plan Increase ${addOnOrdinal}`;
                                  return (
                                    <Paper
                                      key={grant.id}
                                      radius="sm"
                                      p="md"
                                      bg="var(--mantine-color-blue-1)"
                                    >
                                      <Group
                                        justify="space-between"
                                        wrap="nowrap"
                                        align="flex-start"
                                      >
                                        <Stack gap={2}>
                                          <Text size="sm" fw={600}>
                                            {blockLabel}
                                          </Text>
                                          <Text size="xs" c="blue.9">
                                            {grant.committedCreditUnits.toLocaleString()}{" "}
                                            credits @{" "}
                                            {formatUsdFromCents(
                                              grant.unitPriceCents
                                            )}
                                            /credit
                                          </Text>
                                        </Stack>
                                        <Text size="sm" fw={600}>
                                          {formatUsdFromCents(
                                            grant.committedValueCents
                                          )}
                                        </Text>
                                      </Group>
                                    </Paper>
                                  );
                                }
                              )}
                            </Stack>
                          </>
                        )}
                    </Paper>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          ) : (
            <Flex gap="xl" justify="flex-start" w="100%" p="sm">
              {/* Prepaid Usage Progress Bar - show currentCredits out of total */}
              {showProgressBar && (
                <Stack gap={4} w="100%">
                  <Flex gap="xs" align="center" justify="space-between">
                    <Text size="md" fw={600}>
                      {currentCredits} / {totalCreditsForMeter} returns
                      remaining
                    </Text>
                    {prepaidUsage > 0 && (
                      <Text size="xs" c="dimmed">
                        {prepaidUsage} used this period
                      </Text>
                    )}
                  </Flex>

                  <Progress.Root radius="sm" size="md">
                    <Tooltip
                      label={`${businessPoolCredits} plan credits remaining`}
                      withArrow
                    >
                      <Progress.Section
                        value={businessPoolPercentage}
                        color="blue"
                      />
                    </Tooltip>
                    {hasTeamMemberCredits && (
                      <Tooltip
                        label={`${teamMemberCredits} team member credits`}
                        withArrow
                      >
                        <Progress.Section
                          value={teamMemberPercentage}
                          color="teal.5"
                        />
                      </Tooltip>
                    )}
                    <Tooltip label={`${prepaidUsage} used`} withArrow>
                      <Progress.Section
                        value={usagePercentage}
                        color="gray.3"
                      />
                    </Tooltip>
                  </Progress.Root>

                  {/* Legend for credit types */}
                  {hasTeamMemberCredits && (
                    <Group gap="md" mt={4}>
                      <Group gap={4}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: "var(--mantine-color-blue-6)",
                          }}
                        />
                        <Text size="xs" c="dimmed">
                          Plan credits ({businessPoolCredits})
                        </Text>
                      </Group>
                      <Group gap={4}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: "var(--mantine-color-teal-5)",
                          }}
                        />
                        <Text size="xs" c="dimmed">
                          <IconUsers
                            size={12}
                            style={{ verticalAlign: "middle", marginRight: 2 }}
                          />
                          Team member credits ({teamMemberCredits})
                        </Text>
                      </Group>
                    </Group>
                  )}
                </Stack>
              )}
              {showPayLaterUsage && showProgressBar && (
                <Divider orientation="vertical" c="gray" />
              )}
              {/* Pay Later Usage Counter - only show if payLaterUsage > 0 */}
              {showPayLaterUsage && (
                <Group gap="sm" align="center" pl="lg">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <ThemeIcon variant="light" size="sm" radius="xl">
                        <IconFolderBolt size={48} />
                      </ThemeIcon>
                      <Text size="2rem" c="#0077CC" fw={800}>
                        {payLaterUsage}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" fw={500}>
                      pay-as-you-go {payLaterUsage === 1 ? "return" : "returns"}{" "}
                      this period
                    </Text>
                  </Stack>
                </Group>
              )}
            </Flex>
          )}
        </Paper>
        {isContractCommitment && onPurchaseCredits && (
          <Paper radius="md" p="lg" shadow="none" withBorder>
            <Group justify="space-between" mt={4}>
              <div>
                <Text size="md" fw={500}>
                  Adjust Your Plan
                </Text>
                <Text size="xs">Increase your plan to add more returns.</Text>
              </div>
              <Button variant="outline" onClick={onPurchaseCredits} size="sm">
                Increase
              </Button>
            </Group>
          </Paper>
        )}
        {isContractCommitment && onPurchaseCommercialAddOns && (
          <Paper radius="md" p="lg" shadow="none" withBorder>
            <Stack gap="sm" mt={4}>
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div>
                  <Text size="md" fw={500}>
                    Add-on Features
                  </Text>
                  <Text size="xs">Add-on features to your plan.</Text>
                </div>
                <Button
                  variant="outline"
                  onClick={onPurchaseCommercialAddOns}
                  size="sm"
                >
                  Add
                </Button>
              </Group>
              {appliedCommercialAddOns.length > 0 ? (
                <div>
                  <Text size="xs" c="dimmed" fw={500} mb={6}>
                    On your plan:
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    {appliedCommercialAddOns.map((row) => {
                      const imageSrc = commercialAddOnAvatarSrc(row);
                      return (
                        <Badge
                          key={row.id}
                          variant="outline"
                          color="gray"
                          size="xl"
                          tt="none"
                          c="black"
                          leftSection={
                            <Avatar
                              src={imageSrc}
                              alt=""
                              size={28}
                              radius="sm"
                              color="teal"
                              variant="light"
                              p={4}
                            >
                              <IconPackage size={16} stroke={1.5} />
                            </Avatar>
                          }
                          styles={{
                            label: { textTransform: "none" },
                            root: { paddingLeft: 6 },
                          }}
                        >
                          {row.title}
                        </Badge>
                      );
                    })}
                  </Group>
                </div>
              ) : null}
            </Stack>
          </Paper>
        )}
        <Paper radius="md" p="lg" shadow="none" withBorder>
          <Group justify="space-between" mt={4}>
            <div>
              <Text size="md" fw={500}>
                Billing Portal
              </Text>
              <Text size="xs">
                Manage your billing information and payment methods.
              </Text>
            </div>
            <Button variant="outline" onClick={onManageBilling} size="sm">
              Open
            </Button>
          </Group>
        </Paper>
      </Stack>
    </Paper>
  );
};
